import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import { toErrorMessage } from "@src/shared/utils/errors";
import { events } from "@src/shared/utils/observability/events";
import { captureException } from "@src/shared/utils/observability/sentry";

import { authClient } from "../../data";
import type {
  TwoFactorEnrollment,
  TwoFactorEnrollmentMethod,
} from "../../domain/v2";

const TWO_FACTOR_STATUS_QUERY_KEY = ["auth", "two-factor", "status"] as const;

type BetterAuthResult<TData> = {
  data: TData | null;
  error: { message?: string | undefined } | null;
};

function readError(
  result: BetterAuthResult<unknown>,
  fallback: string,
): string | null {
  if (!result.error) return null;

  return result.error.message?.trim() || fallback;
}

/**
 * Drives the two-factor section of the security settings page.
 *
 * Enrolment is two calls, not one: `enable` provisions the secret and backup
 * codes but leaves 2FA off, then verifying a code is what flips
 * `twoFactorEnabled` server-side. `enrollment` is non-null between those two
 * steps, which is what the UI uses to show the verification step.
 *
 * That code can come from the authenticator app `enable` provisions or, for
 * someone who doesn't want one, from `sendEmailCode`. Better Auth accepts either
 * against the session held during enrolment, so `method` decides which endpoint
 * confirms it. Choosing the emailed method sends the first code straight away,
 * since it is useless without one.
 *
 * Status comes from a React Query around `getSession` rather than
 * `authClient.useSession()`: Better Auth's session store fetches once per page
 * load and then only refetches when its own signal fires, so a component that
 * mounts later reads a stale `twoFactorEnabled`.
 */
export function useTwoFactorEnrollment() {
  const queryClient = useQueryClient();
  const {
    data: status,
    isLoading,
    isError: isStatusUnavailable,
    refetch: refetchStatus,
  } = useQuery({
    queryKey: TWO_FACTOR_STATUS_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await authClient.getSession({
        query: { disableCookieCache: true },
      });

      // Throwing rather than falling through to `false` is the point: a failed
      // check must not be indistinguishable from "2FA really is off", or we'd
      // offer to enable it for someone who already has it on.
      if (error) {
        captureException(error, {
          tags: { operation: "twoFactorStatus", route: "v2/settings/security" },
        });

        throw new Error(
          error.message?.trim() || "Unable to check two-factor status.",
        );
      }

      return {
        // Named in the UI so someone choosing emailed codes can see where they
        // will land before committing to them.
        email: data?.user.email ?? null,
        isEnabled: data?.user.twoFactorEnabled === true,
      };
    },
    retry: false,
  });
  const [enrollment, setEnrollment] = useState<TwoFactorEnrollment | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [method, setMethod] = useState<TwoFactorEnrollmentMethod>("totp");
  const [emailCodeSent, setEmailCodeSent] = useState(false);
  const [isSendingEmailCode, setIsSendingEmailCode] = useState(false);
  const [isStartingEnrollment, setIsStartingEnrollment] = useState(false);
  const [isConfirmingEnrollment, setIsConfirmingEnrollment] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);

  const refreshStatus = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: TWO_FACTOR_STATUS_QUERY_KEY,
    });
  }, [queryClient]);

  /**
   * Emails a code so enrolment can be finished without an authenticator app.
   * Better Auth answers 200 even when delivery fails, so the UI has to leave the
   * request repeatable rather than treat this as proof the code arrived.
   */
  const sendEmailCode = useCallback(async () => {
    setIsSendingEmailCode(true);
    setError(null);

    try {
      const result = await authClient.twoFactor.sendOtp();
      const message = readError(result, "Unable to email a code.");

      if (message) {
        setError(message);
        return;
      }

      setEmailCodeSent(true);
      setMethod("otp");
    } catch (err) {
      setError(toErrorMessage(err));
    } finally {
      setIsSendingEmailCode(false);
    }
  }, []);

  const startEnrollment = useCallback(
    async (password: string, chosenMethod: TwoFactorEnrollmentMethod) => {
      setIsStartingEnrollment(true);
      setError(null);
      setMethod(chosenMethod);
      setEmailCodeSent(false);

      try {
        const result = await authClient.twoFactor.enable({ password });
        const message = readError(result, "Unable to start two-factor setup.");

        if (message || !result.data) {
          setError(message ?? "Unable to start two-factor setup.");
          return;
        }

        setEnrollment({
          backupCodes: result.data.backupCodes,
          totpUri: result.data.totpURI,
        });

        if (chosenMethod === "otp") await sendEmailCode();
      } catch (err) {
        setError(toErrorMessage(err));
      } finally {
        setIsStartingEnrollment(false);
      }
    },
    [sendEmailCode],
  );

  /**
   * Goes back to the authenticator app after asking for an emailed code. The
   * secret is already provisioned, so this is only a change of mind about which
   * code to type.
   */
  const selectAuthenticatorApp = useCallback(() => {
    setMethod("totp");
    setError(null);
  }, []);

  const confirmEnrollment = useCallback(
    async (code: string) => {
      setIsConfirmingEnrollment(true);
      setError(null);

      try {
        const result = await (method === "otp"
          ? authClient.twoFactor.verifyOtp({ code })
          : authClient.twoFactor.verifyTotp({ code }));
        const message = readError(result, "That code didn't match. Try again.");

        if (message) {
          setError(message);
          events.enableTwoFactor.track({
            method,
            success: false,
            error: message,
          });
          return;
        }

        events.enableTwoFactor.track({ method, success: true });
        // Refresh before clearing `enrollment`, not after: the verification step
        // is keyed off `enrollment`, so dropping it while the status query is
        // still refetching renders the "Off" card for a beat.
        await refreshStatus();
        setEnrollment(null);
      } catch (err) {
        const message = toErrorMessage(err);

        setError(message);
        events.enableTwoFactor.track({
          method,
          success: false,
          error: message,
        });
      } finally {
        setIsConfirmingEnrollment(false);
      }
    },
    [method, refreshStatus],
  );

  const cancelEnrollment = useCallback(() => {
    setEnrollment(null);
    setError(null);
    setMethod("totp");
    setEmailCodeSent(false);
  }, []);

  const disable = useCallback(
    async (password: string) => {
      setIsDisabling(true);
      setError(null);

      try {
        const result = await authClient.twoFactor.disable({ password });
        const message = readError(
          result,
          "Unable to turn off two-factor authentication.",
        );

        if (message) {
          setError(message);
          events.disableTwoFactor.track({ success: false, error: message });
          return;
        }

        setEnrollment(null);
        events.disableTwoFactor.track({ success: true });
        await refreshStatus();
      } catch (err) {
        const message = toErrorMessage(err);

        setError(message);
        events.disableTwoFactor.track({ success: false, error: message });
      } finally {
        setIsDisabling(false);
      }
    },
    [refreshStatus],
  );

  return {
    cancelEnrollment,
    confirmEnrollment,
    disable,
    email: status?.email ?? null,
    emailCodeSent,
    enrollment,
    error,
    isConfirmingEnrollment,
    isDisabling,
    isEnabled: status?.isEnabled ?? false,
    isLoading,
    isSendingEmailCode,
    isStartingEnrollment,
    isStatusUnavailable,
    method,
    retryStatus: refetchStatus,
    selectAuthenticatorApp,
    sendEmailCode,
    startEnrollment,
  } as const;
}
